import { useEffect, useRef, useState } from "react";
import { completeEmbedLogin, embedTransaction } from "@/lib/embed-auth";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLogin, primeAuth, AUTH_ME_KEY } from "@/lib/auth";
import { MasterLoginRequiredError, AccountLockedError, MfaCodeRequiredError, MfaInvalidCodeError, MfaSetupRequiredError } from "@/lib/auth-errors";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@/components/ui/input-otp";
import { useOrgBySlug } from "@/lib/auth";
import { resolvePostAuthTarget } from "@/lib/post-auth-redirect";
import { useToast } from "@/hooks/use-toast";
import { useBranding } from "@/hooks/use-branding";
import { getAuthToken, setAuthToken, queryClient } from "@/lib/queryClient";
import { Eye, EyeOff, Lock, ArrowRight, ShieldCheck, ShieldAlert, Info } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Small helper that renders a field label with an optional info tooltip.
 * The tooltip icon is keyboard-accessible (focusable button) and screen-reader
 * friendly (aria-label mirrors the tooltip text). The tooltip opens on hover
 * AND on keyboard focus so keyboard-only users can access the hint.
 * Pass required={true} to render a red asterisk after the label text.
 */
function FieldLabel({
  htmlFor,
  label,
  tip,
  required,
}: {
  htmlFor: string;
  label: string;
  tip?: string;
  required?: boolean;
}) {
  const [tipOpen, setTipOpen] = useState(false);
  return (
    <div className="flex items-center gap-1">
      <Label htmlFor={htmlFor}>
        {label}
        {required && (
          <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>
        )}
      </Label>
      {tip && (
        <Tooltip open={tipOpen} onOpenChange={setTipOpen}>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={tip}
              className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              tabIndex={0}
              onFocus={() => setTipOpen(true)}
              onBlur={() => setTipOpen(false)}
            >
              <Info className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 cursor-help" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-[220px] text-xs leading-snug">
            {tip}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * Self-contained sign-in / sign-up form. Used both inline on the landing page
 * (inside a dialog) and as the /login route fallback. Handles password login,
 * internal-org SSO (?org=slug), and self-serve signup → plan selection.
 */
export default function AuthPanel({ initialMode = "signin" }: { initialMode?: "signin" | "signup" }) {
  const branding = useBranding();
  const { toast } = useToast();
  const loginMutation = useLogin();

  const [mode, setMode] = useState<"signin" | "signup">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [orgSlugInput, setOrgSlugInput] = useState("");
  const [signingUp, setSigningUp] = useState(false);
  // Persistent inline form error (WCAG 3.3.1): announced via role="alert",
  // associated with the fields via aria-describedby, and it does not vanish
  // like a toast. Cleared on the next submit attempt.
  const [formError, setFormError] = useState("");
  // Remember Me: when unchecked the JWT is stored in sessionStorage (cleared
  // when the browser is closed) instead of localStorage (persists across sessions).
  // No new auth mechanism — this is purely client-side storage routing.
  const [rememberMe, setRememberMe] = useState(true);
  // Legal acceptance — both must be checked before signup can proceed.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  // MFA login step — credentials (default) → totp | setup-required | locked
  const [loginStep, setLoginStep] = useState<"credentials" | "totp" | "setup-required" | "locked">("credentials");
  const [totpCode, setTotpCode] = useState("");
  const [lockoutMessage, setLockoutMessage] = useState("");

  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const orgSlug = params.get("org");
  const { data: orgInfo } = useOrgBySlug(orgSlug);
  const isInternalOrg = orgInfo?.mode === "internal";
  const ssoEnforced = orgInfo?.ssoEnforced === "true";

  const transactionId = embedTransaction(window.location.search);
  const [embedCompleting, setEmbedCompleting] = useState(false);
  const embedCompletionRef = useRef(false);
  const finishEmbed = async () => {
    if (!transactionId || embedCompletionRef.current) return;
    embedCompletionRef.current = true;
    setEmbedCompleting(true);
    try {
      await completeEmbedLogin(transactionId);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "This sign-in could not authorize the bot. Close this window and restart sign-in from the widget.");
      embedCompletionRef.current = false;
      redirectedRef.current = false;
      setEmbedCompleting(false);
    }
  };
  useEffect(() => {
    // An already authenticated platform session still needs bot authorization.
    if (transactionId && getAuthToken()) void finishEmbed();
  }, [transactionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSsoSignIn = (slug?: string) => {
    const target = slug || orgSlug || orgSlugInput.trim();
    if (!target) {
      toast({ title: "Enter your organization slug to continue with SSO", variant: "destructive" });
      return;
    }
    const returnTo = transactionId ? `/login?embed_transaction=${encodeURIComponent(transactionId)}` : null;
    window.location.href = `/sso/login/${encodeURIComponent(target)}${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`;
  };

  const [, navigate] = useLocation();
  // Post-auth navigation is a client-side route change (no full reload):
  // useLogin resolves only AFTER /api/auth/me has been fetched into the
  // cache, so guards and the target dashboard mount against fully
  // initialized user/org/permission/feature state. `replace` keeps the
  // sign-in page out of history, and the ref makes the redirect fire at
  // most once even if the form resolves twice (double Enter etc.).
  const redirectedRef = useRef(false);
  const redirectTarget = params.get("redirect");
  const goAfterAuth = (fallback = "/") => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;

    if (transactionId) {
      void finishEmbed();
      return;
    }

    // primeAuth() has already completed at this point, so the user's role is
    // in the query cache. Read it synchronously to pick the correct home URL
    // without an extra round-trip or re-render cycle.
    const me = queryClient.getQueryData<{ user?: { role?: string }; organization?: { needsRolesOnboarding?: boolean } }>(
      AUTH_ME_KEY as unknown as readonly string[],
    );
    const role = me?.user?.role;

    // Resume interrupted onboarding: new orgs that closed the tab before
    // completing role selection are sent straight back to the roles step.
    if (me?.organization?.needsRolesOnboarding) {
      navigate("/onboarding/roles", { replace: true });
      return;
    }

    const home = role === "super_admin" ? "/admin" : fallback;
    navigate(resolvePostAuthTarget(redirectTarget, home), { replace: true });
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loginMutation.isPending || redirectedRef.current) return; // no duplicate submits/redirects
    // WCAG 3.3.1: announce inline validation errors before any network call so
    // screen readers hear the `role="alert"` text, not just the browser tooltip.
    if (!email.trim()) { setFormError("Email or username is required."); return; }
    if (!password) { setFormError("Password is required."); return; }
    setFormError("");
    try {
      await loginMutation.mutateAsync({ email, password });
      // Remember Me = unchecked: move the JWT + refresh token from
      // localStorage to sessionStorage so the session clears when the
      // browser is closed without changing the auth mechanism or backend.
      // getAuthToken() reads sessionStorage first, so subsequent calls
      // (including setAuthToken on token rotation) stay in sessionStorage.
      if (!rememberMe) {
        try {
          const tok = localStorage.getItem("dotnetbot.jwt");
          const rt  = localStorage.getItem("dotnetbot.refresh");
          if (tok) {
            sessionStorage.setItem("dotnetbot.jwt.session", tok);
            localStorage.removeItem("dotnetbot.jwt");
          }
          if (rt) {
            sessionStorage.setItem("dotnetbot.refresh.session", rt);
            localStorage.removeItem("dotnetbot.refresh");
          }
        } catch { /* storage unavailable — ignore; session will use localStorage */ }
      }
      goAfterAuth();
    } catch (err: any) {
      // Master admins can't use the regular form — bounce them to the
      // dedicated two-step (password + OTP) sign-in flow instead of
      // surfacing a dead-end error.
      if (err instanceof MasterLoginRequiredError) {
        toast({ title: "Master admin sign-in", description: "Redirecting to the secure master sign-in…" });
        window.location.href = err.redirect || "/master/login";
        return;
      }
      if (err instanceof AccountLockedError) {
        setLockoutMessage(err.message);
        setLoginStep("locked");
        return;
      }
      if (err instanceof MfaCodeRequiredError) {
        setLoginStep("totp");
        return;
      }
      if (err instanceof MfaSetupRequiredError) {
        setLoginStep("setup-required");
        return;
      }
      setFormError(err.message || "Authentication failed");
    }
  };

  // Handles the second-factor step: re-sends credentials + TOTP code.
  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loginMutation.isPending || redirectedRef.current) return;
    if (totpCode.length !== 6) { setFormError("Enter the 6-digit code from your authenticator app."); return; }
    setFormError("");
    try {
      await loginMutation.mutateAsync({ email, password, totpCode });
      goAfterAuth();
    } catch (err: any) {
      if (err instanceof MfaInvalidCodeError) {
        setFormError("Invalid or expired code. Please try again.");
        setTotpCode("");
        return;
      }
      if (err instanceof AccountLockedError) {
        setLockoutMessage(err.message);
        setLoginStep("locked");
        return;
      }
      setFormError(err.message || "Verification failed");
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signingUp) return;
    // WCAG 3.3.1: surface required-field errors programmatically before the
    // network call so the role="alert" paragraph announces them to screen readers.
    if (!fullName.trim()) { setFormError("Full name is required."); return; }
    if (!email.trim()) { setFormError("Email is required."); return; }
    if (!password) { setFormError("Password is required."); return; }
    if (password.length < 8) { setFormError("Password must be at least 8 characters."); return; }
    if (!acceptedTerms) { setFormError("Please accept the Terms & Conditions to continue."); return; }
    if (!acceptedPrivacy) { setFormError("Please acknowledge the Privacy Policy to continue."); return; }
    setFormError("");
    setSigningUp(true);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName, organizationName, email, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Sign-up failed");
      if (body?.token) setAuthToken(body.token);
      // Fully hydrate auth state before routing — the billing lock in
      // AuthenticatedApp needs organization.requiresBilling to be present.
      await primeAuth();
      toast({ title: "Welcome!", description: "Choose which roles to enable for your organization." });
      navigate("/onboarding/roles", { replace: true });
    } catch (err: any) {
      setFormError(err?.message || "Sign-up failed. Try again.");
    } finally {
      setSigningUp(false);
    }
  };

  const passwordField = (autoComplete: string, isSignup = false) => (
    <div className="space-y-1.5">
      <FieldLabel
        htmlFor="auth-password"
        label="Password"
        tip={isSignup ? "Must be at least 8 characters. A mix of letters, numbers, and symbols makes it stronger." : undefined}
        required
      />
      <div className="relative">
        <Input
          id="auth-password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete={autoComplete}
          aria-invalid={formError ? true : undefined}
          aria-describedby={formError ? "auth-form-error" : undefined}
          minLength={mode === "signup" ? 8 : undefined}
          placeholder={mode === "signup" ? "At least 8 characters" : "Password"}
          className="pr-10"
          data-testid="input-password"
        />
        <button
          type="button"
          onClick={() => setShowPassword((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          aria-label="Toggle password visibility"
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  if (embedCompleting) {
    return <div role="status" className="space-y-3 py-6 text-center">
      <div className="h-3 rounded bg-slate-100 animate-pulse" />
      <p className="text-sm text-slate-500">Checking access to your bot…</p>
    </div>;
  }

  // MFA challenge steps — replace the credentials form until the factor is resolved.
  // This renders for both internal-org and regular users, so it lives before both branches.
  if (loginStep !== "credentials") {
    return (
      <div className="space-y-4">
        {/* ── TOTP step ───────────────────────────────────────────────────── */}
        {loginStep === "totp" && (
          <form onSubmit={handleTotpSubmit} noValidate className="space-y-5" data-testid="form-totp">
            <div className="text-center space-y-2">
              <div className="w-11 h-11 rounded-full bg-indigo-100 flex items-center justify-center mx-auto">
                <ShieldCheck className="w-5 h-5 text-indigo-600" aria-hidden="true" />
              </div>
              <h3 className="font-semibold text-slate-900">Two-factor authentication</h3>
              <p className="text-sm text-slate-500">
                Enter the 6-digit code from your authenticator app.
              </p>
            </div>

            <div className="flex justify-center" role="group" aria-label="One-time password">
              <InputOTP
                maxLength={6}
                value={totpCode}
                onChange={(v) => { setTotpCode(v); setFormError(""); }}
                autoFocus
                data-testid="input-totp"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            {formError && (
              <p role="alert" className="text-sm font-medium text-destructive text-center" data-testid="text-totp-error">
                {formError}
              </p>
            )}

            <Button
              type="submit"
              disabled={loginMutation.isPending || totpCode.length !== 6}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="button-totp-submit"
            >
              {loginMutation.isPending ? "Verifying…" : "Verify code"}
            </Button>

            <button
              type="button"
              onClick={() => { setLoginStep("credentials"); setTotpCode(""); setFormError(""); }}
              className="w-full text-sm text-slate-500 hover:text-slate-700 text-center underline-offset-2 hover:underline"
              data-testid="button-totp-back"
            >
              ← Back to sign in
            </button>
          </form>
        )}

        {/* ── MFA setup required ──────────────────────────────────────────── */}
        {loginStep === "setup-required" && (
          <div className="space-y-5 text-center" data-testid="panel-mfa-setup-required">
            <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
              <ShieldAlert className="w-5 h-5 text-amber-600" aria-hidden="true" />
            </div>
            <div className="space-y-1">
              <h3 className="font-semibold text-slate-900">MFA setup required</h3>
              <p className="text-sm text-slate-500">
                Your organization requires two-factor authentication, but your account is not enrolled.
                Contact your IT administrator to arrange secure MFA enrollment or account recovery,
                then restart sign-in. Access cannot be granted until enrollment is complete.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setLoginStep("credentials"); setFormError(""); }}
              className="w-full text-sm text-slate-500 hover:text-slate-700 text-center underline-offset-2 hover:underline"
              data-testid="button-setup-back"
            >
              Restart sign-in
            </button>
          </div>
        )}

        {/* ── Account locked ──────────────────────────────────────────────── */}
        {loginStep === "locked" && (
          <div className="space-y-5 text-center" data-testid="panel-account-locked">
            <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <Lock className="w-5 h-5 text-red-600" aria-hidden="true" />
            </div>
            <div className="space-y-1">
              <h3 className="font-semibold text-slate-900">Account temporarily locked</h3>
              <p className="text-sm text-slate-500" role="alert" data-testid="text-locked-message">
                {lockoutMessage}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setLoginStep("credentials"); setLockoutMessage(""); setFormError(""); }}
              className="w-full text-sm text-indigo-600 hover:text-indigo-800 font-medium text-center underline-offset-2 hover:underline"
              data-testid="button-locked-back"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    );
  }

  // Internal-org SSO mode replaces the normal form entirely.
  if (isInternalOrg) {
    return (
      <div className="space-y-4">
        <div className="text-center space-y-1">
          {orgInfo?.logo ? (
            <img src={orgInfo.logo} alt={orgInfo.name} className="h-10 mx-auto mb-1 object-contain" data-testid="img-org-logo" />
          ) : null}
          <h2 className="text-xl font-semibold" data-testid="text-login-title">{orgInfo?.name}</h2>
          <p className="text-sm text-slate-500">Sign in with your work account</p>
        </div>
        {formError && <p role="alert" className="text-sm font-medium text-destructive">{formError}</p>}
        <Button onClick={() => handleSsoSignIn()} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" size="lg" data-testid="button-internal-sso-signin">
          <Lock className="w-4 h-4 mr-2" />Continue with SSO
        </Button>
        {!ssoEnforced && (
          <>
            <div className="flex items-center gap-2 my-1">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-slate-400 text-xs">or sign in with password</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            {/* noValidate: suppress browser native tooltip so our role="alert" fires (WCAG 3.3.1) */}
            <form onSubmit={handleSignIn} noValidate className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="auth-email">Email<span className="text-red-500 ml-0.5" aria-hidden="true">*</span></Label>
                <Input id="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" placeholder="you@company.com" data-testid="input-email" />
              </div>
              {passwordField("current-password")}
              <div className="flex justify-end -mt-1">
                <Link href="/forgot-password" className="text-xs font-medium text-indigo-600 hover:underline" data-testid="link-forgot-password-sso">
                  Forgot password?
                </Link>
              </div>
              <Button type="submit" disabled={loginMutation.isPending} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="button-login-submit">
                {loginMutation.isPending ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            </form>
          </>
        )}
        <p className="text-slate-400 text-xs text-center pt-1">Need help? Contact your IT administrator.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-semibold" data-testid="text-login-title">
          {mode === "signup" ? "Start your free trial" : "Welcome back"}
        </h2>
        <p className="text-sm text-slate-500">
          {mode === "signup"
            ? "No card required — choose a plan after to unlock the workspace."
            : `Sign in to your ${branding.productName} workspace`}
        </p>
        {!transactionId && <div className="inline-flex self-center rounded-lg border border-slate-200 bg-slate-50 p-1 text-sm">
          <button
            type="button"
            onClick={() => setMode("signin")}
            className={`px-4 py-1.5 rounded-md font-medium transition-colors ${mode === "signin" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-signin"
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={() => setMode("signup")}
            className={`px-4 py-1.5 rounded-md font-medium transition-colors ${mode === "signup" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-signup"
          >
            Sign up
          </button>
        </div>}
      </div>

      {mode === "signin" ? (
        <>
          {/* noValidate: suppress browser native tooltip so our role="alert" fires (WCAG 3.3.1) */}
          <form onSubmit={handleSignIn} noValidate className="space-y-4">
            <div className="space-y-1.5">
              <FieldLabel
                htmlFor="auth-email"
                label="Email or username"
                tip="Sign in with your email address or your account username."
                required
              />
              <Input id="auth-email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" placeholder="you@company.com" aria-invalid={formError ? true : undefined} aria-describedby={formError ? "auth-form-error" : undefined} data-testid="input-email" />
            </div>
            {passwordField("current-password")}
            {formError && (
              <p id="auth-form-error" role="alert" className="text-sm font-medium text-destructive" data-testid="text-signin-error">
                {formError}
              </p>
            )}
            <div className="flex items-center justify-between -mt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  id="auth-remember-me"
                  checked={rememberMe}
                  onCheckedChange={(c) => setRememberMe(!!c)}
                  data-testid="checkbox-remember-me"
                />
                <span className="text-sm text-slate-600">Remember me</span>
              </label>
              <Link href="/forgot-password" className="text-xs font-medium text-indigo-600 hover:underline" data-testid="link-forgot-password">
                Forgot password?
              </Link>
            </div>
            <Button type="submit" disabled={loginMutation.isPending} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="button-login-submit">
              {loginMutation.isPending ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
          </form>

          <div className="flex items-center gap-2 my-1">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-slate-400 text-xs">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* WCAG 1.3.1 / 2.4.6: every input must have a persistent programmatically-associated
              label. Placeholder text disappears on input and is never read by screen readers
              as a persistent label. sr-only keeps the UI compact while satisfying the requirement. */}
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <label htmlFor="auth-sso-slug" className="sr-only">
                Organization SSO slug
              </label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Your organization's unique identifier used for single sign-on. Contact your IT admin if you're unsure."
                    className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    tabIndex={0}
                  >
                    <Info className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 cursor-help" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[220px] text-xs leading-snug">
                  Your organization's unique identifier for single sign-on. Contact your IT admin if you're unsure what it is.
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex gap-2">
              <Input
                id="auth-sso-slug"
                value={orgSlugInput}
                onChange={(e) => setOrgSlugInput(e.target.value)}
                placeholder="your-org-slug"
                className="flex-1"
                aria-describedby="auth-sso-slug-hint"
                autoComplete="organization"
                data-testid="input-sso-slug"
              />
              <Button type="button" variant="outline" onClick={() => handleSsoSignIn()} data-testid="button-sso-signin">
                <Lock className="w-4 h-4 mr-2" />SSO
              </Button>
            </div>
            <p id="auth-sso-slug-hint" className="sr-only">
              Enter your organization&apos;s URL slug to sign in with single sign-on
            </p>
          </div>
        </>
      ) : (
        // eslint-disable-next-line -- noValidate suppresses browser tooltip; JS validation fires role="alert" (WCAG 3.3.1)
        <form onSubmit={handleSignUp} noValidate className="space-y-4">
          <div className="space-y-1.5">
            <FieldLabel
              htmlFor="auth-fullName"
              label="Full name"
              tip="Your display name visible to teammates in your workspace."
              required
            />
            <Input
              id="auth-fullName"
              value={fullName}
              onChange={(e) => {
                const v = e.target.value;
                // Title-case: capitalize the first letter of every word so
                // "john smith" → "John Smith" as the user types.
                setFullName(v.replace(/(^|\s)\S/g, (c) => c.toUpperCase()));
              }}
              required
              autoComplete="name"
              placeholder="Jane Doe"
              data-testid="input-signup-name"
            />
          </div>
          <div className="space-y-1.5">
            <FieldLabel
              htmlFor="auth-orgName"
              label="Organization name"
              tip="Your company or team name — this becomes your workspace. You can rename it later."
              required
            />
            <Input
              id="auth-orgName"
              value={organizationName}
              onChange={(e) => {
                const v = e.target.value;
                // Title-case: capitalize the first letter of every word so
                // "acme corporation ltd" → "Acme Corporation Ltd" as the user types.
                setOrganizationName(v.replace(/(^|\s)\S/g, (c) => c.toUpperCase()));
              }}
              required
              autoComplete="organization"
              placeholder="Acme Inc."
              data-testid="input-signup-org"
            />
          </div>
          <div className="space-y-1.5">
            <FieldLabel
              htmlFor="auth-email"
              label="Work email"
              tip="Use your work email so your account stays linked to your organization."
              required
            />
            <Input id="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" placeholder="you@company.com" data-testid="input-signup-email" />
          </div>
          {passwordField("new-password", true)}

          {/* Legal acceptance — both must be checked before the account is created */}
          <div className="space-y-2 pt-1">
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                id="auth-terms"
                checked={acceptedTerms}
                onCheckedChange={(c) => setAcceptedTerms(!!c)}
                className="mt-0.5"
                data-testid="checkbox-terms"
              />
              <span className="text-xs text-slate-600 leading-relaxed">
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium">
                  Terms &amp; Conditions
                </a>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                id="auth-privacy"
                checked={acceptedPrivacy}
                onCheckedChange={(c) => setAcceptedPrivacy(!!c)}
                className="mt-0.5"
                data-testid="checkbox-privacy"
              />
              <span className="text-xs text-slate-600 leading-relaxed">
                I acknowledge the{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium">
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>

          {formError && (
            <p id="auth-form-error" role="alert" className="text-sm font-medium text-destructive" data-testid="text-signup-error">
              {formError}
            </p>
          )}
          <Button type="submit" disabled={signingUp} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="button-signup-submit">
            {signingUp ? "Creating workspace…" : (<>Continue <ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>
        </form>
      )}
    </div>
  );
}
